/**
 * Repair a provider's mislabeled ADTS audio codec before it reaches MSE.
 *
 * WHY THIS EXISTS (measured 2026-09-21 on the live provider):
 *
 * Two live channels — USA AHC UHD and USA AMC UHD — played in the native app
 * (ExoPlayer) but failed in every browser with "The stream could not be
 * loaded.", retrying forever. The manifests and segments were fine: byte for
 * byte they are H.264 + HE-AAC MPEG-TS, and `ffprobe` reports HE-AAC on the
 * same segments.
 *
 * The blocker is the ADTS header. HE-AAC (AOT 5) does not fit in ADTS's
 * two-bit `profile` field, so encoders that signal SBR explicitly write
 * `profile = 00` instead, which decodes as MPEG-4 Audio Object Type 1 — "AAC
 * Main". hls.js derives the MSE codec string straight from those bits
 * (`'mp4a.40.' + (((byte2 >> 6) & 0x3) + 1)`), so it asks for
 * `audio/mp4;codecs=mp4a.40.1`, and Chromium refuses:
 *
 *   Failed to execute 'addSourceBuffer' on 'MediaSource': The type provided
 *   ('audio/mp4;codecs=mp4a.40.1') is unsupported.
 *
 * hls.js raises that as a FATAL `bufferAddCodecError`, so playback never
 * starts — the file itself is perfectly decodable, only the label is wrong.
 * Verified end to end: rewriting that one call to `mp4a.40.5` took the same
 * channel from readyState 0 to LIVE (readyState 4, 1920px frames, audio
 * decoding).
 *
 * The shim wraps `addSourceBuffer` and rewrites ONLY an audio mime the browser
 * says it cannot support, whose AAC object type is one of the ones neither
 * Chrome nor Firefox expose over MSE (Main 1, SSR 3, LTP 4). Everything else
 * passes through untouched, and if no fallback is supported the original call
 * runs unchanged so the real error still surfaces.
 *
 * Order matters: HE-AAC first, because that is what these streams actually
 * are, and declaring LC on an SBR bitstream throws the high band away. LC is
 * only a last resort for an object type that refuses both.
 */
const MISLABELED_ADTS_AUDIO_MIME = /^(audio\/mp4\s*;\s*codecs\s*=\s*)("?)mp4a\.40\.(\d+)\2$/i;

/** AAC object types browsers expose over MSE. Anything else is the mislabel. */
const SUPPORTED_AAC_OBJECT_TYPES = new Set([2, 5, 29]);

/** Honestly-labeled codecs, best first: HE-AAC (what these streams are), then LC. */
const AAC_FALLBACK_CODECS = ["mp4a.40.5", "mp4a.40.2"] as const;

const PATCH_MARKER = Symbol.for("@hybrid-iptv/player.mseAudioCodecFallback");

export interface MediaSourceConstructorLike {
  isTypeSupported(type: string): boolean;
  prototype: { addSourceBuffer(type: string): unknown };
}

export interface MediaSourceScope {
  MediaSource?: MediaSourceConstructorLike;
  ManagedMediaSource?: MediaSourceConstructorLike;
}

/**
 * Pick the mime type to hand to `addSourceBuffer`.
 *
 * Exported for tests: the rewrite decision is the whole behavior, and it can be
 * exercised without a real MediaSource.
 */
export function rewriteUnsupportedAdtsAudioMime(
  mimeType: string,
  isSupported: (type: string) => boolean,
): string {
  const match = MISLABELED_ADTS_AUDIO_MIME.exec(mimeType);
  if (!match) return mimeType;
  const objectType = Number(match[3]);
  if (SUPPORTED_AAC_OBJECT_TYPES.has(objectType)) return mimeType;
  // Some other object type the browser happens to accept: leave it alone.
  if (isSupported(mimeType)) return mimeType;
  for (const codec of AAC_FALLBACK_CODECS) {
    const candidate = `${match[1]}${match[2]}${codec}${match[2]}`;
    if (isSupported(candidate)) return candidate;
  }
  return mimeType;
}

/**
 * Install the rewrite on the page's MediaSource (and ManagedMediaSource when
 * present). Idempotent per prototype, so it is safe to call from every engine
 * we start, and a no-op outside the browser (Node tests, SSR).
 *
 * Returns true when this call was the one that patched something.
 */
export function installMseAudioCodecFallback(scope: MediaSourceScope = globalThis as MediaSourceScope): boolean {
  const constructors = [scope.MediaSource, scope.ManagedMediaSource].filter(
    (ctor): ctor is MediaSourceConstructorLike =>
      typeof ctor?.prototype?.addSourceBuffer === "function",
  );
  if (constructors.length === 0) return false;
  let installed = false;
  for (const ctor of constructors) {
    const proto = ctor.prototype as { addSourceBuffer(type: string): unknown; [PATCH_MARKER]?: boolean };
    if (proto[PATCH_MARKER]) continue;
    const original = proto.addSourceBuffer;
    proto.addSourceBuffer = function patchedAddSourceBuffer(this: unknown, type: string): unknown {
      return original.call(this, rewriteUnsupportedAdtsAudioMime(String(type), (candidate) => ctor.isTypeSupported(candidate)));
    };
    proto[PATCH_MARKER] = true;
    installed = true;
  }
  return installed;
}
